/*
 * Copyright 2020 Spotify AB
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import {
  Button,
  Checkbox,
  // Divider,
  List,
  ListItem,
  ListItemText,
  makeStyles,
  Theme,
  Typography,
  TextField,
  Box,
  Chip,
  Grid,
  Container,
  Popover,
  InputAdornment,
  IconButton,
  // Select,
  // MenuItem,
} from '@material-ui/core';
import React, { useContext } from 'react';
import { filterGroupsContext } from '../../filter/context';
import SearchIcon from '@material-ui/icons/Search';
import Autocomplete from '@material-ui/lab/Autocomplete';
// import InfoIcon from '@material-ui/icons/Info';
// import Tooltip from '@material-ui/core/Tooltip';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import { useState, useRef } from 'react';
import ClearIcon from '@material-ui/icons/Clear';

const useStyles = makeStyles<Theme>(theme => ({
  filterBox: {
    display: 'flex',
    alignItems: 'center',
    // justifyContent: 'space-between',
    margin: theme.spacing(2, 0, 0, 0),
  },
  filterBoxTitle: {
    marginRight: theme.spacing(1),
    fontWeight: theme.typography.fontWeightBold as any,
  },
  title: {
    margin: theme.spacing(1, 0, 0, 1),
    textTransform: 'uppercase',
    fontSize: 12,
    fontWeight: 'bold',
  },
  checkbox: {
    padding: theme.spacing(0, 1, 0, 1),
  },
  categoriesTitle: {
    padding: theme.spacing(2, 0),
    fontSize: '12px',
    display: 'flex',
    gap: '3px',
    alignItems: 'center',
  },
  popover: {
    pointerEvents: 'none',
  },
  popoverContent: {
    pointerEvents: 'auto',
  },
  IconSize: {
    fontSize: '1rem',
  },
  chipsDiv: {
    justifyContent: 'center',
    flexWrap: 'wrap',
    '& > *': {
      margin: theme.spacing(2, 0, 0, 0),
      '& > *': {
        background: '#EB0A1E',
        border: '0px',
        '& > *': {
          color: 'white',
        },
        '& svg:hover': {
          color: 'white',
        },
      },
    },
  },
  infoTag: {
    display: 'flex',
    marginTop: '10px',
    fontSize: '14px',
    '& label': {
      fontSize: '14px',
      color: '#9B9C9D',
    },
    '& svg': {
      fontSize: '20px',
      color: '#696262',
    },
  },
}));

type Props = {
  availableCategories: string[];
  availableTags: string[];
  availableCsp: string[];
  availableStatus: string[];
  setSelectedSort: Function;
  selectedSort: string;
  selfServiceResultParam?:boolean
};

/**
 * The additional results filter in the sidebar.
 */
export const ResultsFilter = ({
  availableCategories,
  availableTags,
  setSelectedSort,
  selectedSort,
  availableCsp,
  selfServiceResultParam,
}: Props) => {
  const classes = useStyles();
  const [tagsData, setTagsData] = React.useState([] as any);
  const [search, setSearch] = useState('');

  const context = useContext(filterGroupsContext);
  if (!context) {
    throw new Error(`Must be used inside an EntityFilterGroupsProvider`);
  }

  const {
    selectedCategories,
    setSelectedCategories,
    selectedTags,
    setSelectedTags,
    selectedCsp,
    setSelectedCsp,
    onSearch,
  } = context;

  const handleDeleteTags = (tagsToDelete: any) => () => {
    setTagsData((tags: any) => tags.filter((tag: any) => tag !== tagsToDelete));
    setSelectedTags(selectedTags.filter((tag: any) => tag !== tagsToDelete));
  };
  const handleClearFilters = () => {
    setSelectedCategories([]);
    setSelectedTags([]);
    setSelectedCsp([]);
    setTagsData([]);
    setSelectedSort('');
  };

  const [openedPopover, setOpenedPopover] = useState(false);
  const popoverAnchor = useRef(null);

  const popoverEnter = () => {
    setOpenedPopover(true);
  };

  const popoverLeave = () => {
    setOpenedPopover(false);
  };
  const customSearchEntity = (event: any) =>{
    setSearch(event.target.value);
    onSearch(event.target.value);
  };

  return (
    <>
      <div className={classes.filterBox}>
        <Typography variant="body2" className={classes.filterBoxTitle}>
          Refine Results
        </Typography>{' '}
        <Button
          onClick={() => handleClearFilters()}
          disabled={
            selectedCategories.length === 0 &&
            selectedTags.length === 0 &&
            selectedSort === '' &&
            selectedCsp.length === 0
          }
          color="secondary"
        >
          Clear
        </Button>
      </div>
      {/* <Divider /> */}

      
      <Grid container>
        <Grid item>
          <Typography
            variant="button"
            component="div"
            className={classes.categoriesTitle}
          >
            Categories
            {!selfServiceResultParam && <Typography className={classes.IconSize}>
              <span
                ref={popoverAnchor}
                aria-owns="mouse-over-popover"
                aria-haspopup="true"
                onMouseEnter={popoverEnter}
                onMouseLeave={popoverLeave}
                font-Size={'small'}
              >
                <InfoOutlinedIcon
                  className={classes.IconSize}
                ></InfoOutlinedIcon>
              </span>
              <Popover
                id="mouse-over-popover"
                className={classes.popover}
                classes={{
                  paper: classes.popoverContent,
                }}
                open={openedPopover}
                anchorEl={popoverAnchor.current}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'left',
                }}
                PaperProps={{
                  onMouseEnter: popoverEnter,
                  onMouseLeave: popoverLeave,
                }}
              >
                <div style={{ paddingTop: '24px', paddingBottom: '24px' }}>
                  <Container maxWidth="sm">
                    <Typography paragraph>
                      <b>Infrastructure Blueprint</b> is Terraform code that
                      implements a complex infrastructure component/service on
                      top of a Terraform modules to fulfill non-functional
                      requirments, tagging and naming conventions and adhere to
                      TMNA standards and policies.
                    </Typography>
                  </Container>
                  <Container maxWidth="sm">
                    <Typography paragraph>
                      <b>Architecture Blueprints </b> is Terraform code that
                      implements a particular cloud infrastrucure pattern
                      combining several infrastrucure Blueprints or
                      complementing other Architecture Blueprints.
                    </Typography>
                  </Container>
                  <Container maxWidth="sm">
                    <Typography>
                      <b>Reference Architecture</b> is a combination of
                      Architecture/infrastructure blueprints with application
                      code to solve cloud application patterns with specific
                      technologies based on TMNA tech radar.
                    </Typography>
                  </Container>
                </div>
              </Popover>
            </Typography>
            }
          </Typography>
          <List disablePadding dense>
            {availableCategories.map(category => {
              const labelId = `checkbox-list-label-${category}`;
              return (
                <ListItem
                  key={category}
                  dense
                  button
                  onClick={() =>
                    setSelectedCategories(
                      selectedCategories.includes(category)
                        ? selectedCategories.filter(
                            selectedCategory => selectedCategory !== category,
                          )
                        : [...selectedCategories, category],
                    )
                  }
                >
                  <Checkbox
                    edge="start"
                    color="primary"
                    checked={selectedCategories.includes(category)}
                    tabIndex={-1}
                    disableRipple
                    className={classes.checkbox}
                    inputProps={{ 'aria-labelledby': labelId }}
                  />
                  <ListItemText
                    id={labelId}
                    primary={
                      category.charAt(0).toLocaleUpperCase('en-US') +
                      category.slice(1)
                    }
                  />
                </ListItem>
              );
            })}
          </List>
        </Grid>
        {( availableCsp.length > 0  && !selfServiceResultParam) && (
          <Grid item>
            <Typography
              variant="button"
              component="div"
              className={classes.categoriesTitle}
            >
              CSP
            </Typography>
            <List disablePadding dense>
              {availableCsp.map(csp => {
                const labelId = `checkbox-list-label-${csp}`;
                return (
                  <ListItem
                    key={csp}
                    dense
                    button
                    onClick={() => {
                      console.log(selectedCsp, 'h', 'csp', csp);
                      setSelectedCsp(
                        selectedCsp.includes(csp)
                          ? selectedCsp.filter(
                              selectedCsp => selectedCsp !== csp,
                            )
                          : [...selectedCsp, csp],
                      );
                    }}
                  >
                    <Checkbox
                      edge="start"
                      color="primary"
                      checked={selectedCsp.includes(csp)}
                      tabIndex={-1}
                      disableRipple
                      className={classes.checkbox}
                      inputProps={{ 'aria-labelledby': labelId }}
                    />
                    <ListItemText
                      id={labelId}
                      primary={
                        csp.charAt(0).toLocaleUpperCase('en-US') + csp.slice(1)
                      }
                    />
                  </ListItem>
                );
              })}
            </List>
          </Grid>
        )}
        {/* <Grid item>
        <Typography variant="button" component="div" className={classes.categoriesTitle}>
            Status
          </Typography>
          <List disablePadding dense>
            {availableStatus.map(status => {
              const labelId = `checkbox-list-label-${status}`;
              return (
                <ListItem
                  key={status}
                  dense
                  button
                  onClick={() =>
                    setSelectedCategories(
                      selectedCategories.includes(status)
                        ? selectedCategories.filter(
                          selectedCategory => selectedCategory !== status,
                        )
                        : [...selectedCategories, status],
                    )
                  }
                >
                  <Checkbox
                    edge="start"
                    color="primary"
                    checked={selectedCategories.includes(status)}
                    tabIndex={-1}
                    disableRipple
                    className={classes.checkbox}
                    inputProps={{ 'aria-labelledby': labelId }}
                  />
                  <ListItemText
                    id={labelId}
                    primary={
                      status.charAt(0).toLocaleUpperCase('en-US') +
                      status.slice(1)
                    }
                  />
                </ListItem>
              );
            })}
          </List>
        </Grid> */}
        {selfServiceResultParam ? <Grid item>
          <Typography
            variant="button"
            component="div"
            className={classes.categoriesTitle}
          >
            Search Self Service Forms
          </Typography>
          <TextField
            id="input-with-icon-adornment1"
            placeholder="Search for Self Service"
            autoComplete="off"
            onChange={customSearchEntity}
            value={search}
            variant="outlined"
            size="small"
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: search ? (
                <InputAdornment position="end" onClick={() => {setSearch(''),onSearch('')}}>
                  <IconButton>
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              ) : undefined,
            }}
          />
        </Grid>
        :
        <Grid item>
          <Typography
            variant="button"
            component="div"
            className={classes.categoriesTitle}
          >
            Tags
          </Typography>
          <Autocomplete
            multiple
            options={availableTags}
            getOptionLabel={option => option}
            value={tagsData}
            popupIcon={<SearchIcon />}
            style={{ minWidth: '300px' }}
            onChange={(_e, newValue) => {
              setTagsData(newValue);
              setSelectedTags(newValue);
            }}
            renderTags={() => null} // don't render tag in the TextField
            renderInput={params => (
              <TextField
                {...params}
                variant="outlined"
                placeholder="Search Using Tags"
              />
            )}
          />
          {/* <div className={classes.infoTag}>
            <Tooltip title="Search modules using tags, for e.g.:'infrastructure', 'ecs', etc.">
              <InfoIcon />
            </Tooltip>
            <label>Search modules using tags, for e.g.: 'infrastructure', 'ecs', etc.</label>
          </div> */}
          <div className={classes.chipsDiv}>
            <Box>
              {tagsData.map((tag: any) => {
                return (
                  <Chip
                    key={tag}
                    label={tag}
                    // onDelete={data.label === data.label ? undefined : handleDelete(data)}
                    onDelete={handleDeleteTags(tag)}
                    className={classes.chip}
                  />
                );
              })}
            </Box>
          </div>
        </Grid>}
        {/* <Grid item>
        <Select
                value={selectedSort}
                displayEmpty              
                onChange={handleSortChange}           
              >
                <MenuItem value="">
                Sort
              </MenuItem>
                <MenuItem value="AtoZ">A to Z</MenuItem>
                <MenuItem value="ZtoA">Z to A</MenuItem>
                <MenuItem value="Most Popular">Most Popular</MenuItem>
                <MenuItem value="Newest First">Newest to Oldest</MenuItem>
                <MenuItem value="Oldest First">Oldest to Newest</MenuItem>                
              </Select>
        </Grid> */}
      </Grid>
    </>
  );
};
